import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { DispatchViewDefinition } from "@storyboard/dispatch-view/view";
import { useWebSettings } from "@/models/StateProviders/WebSettingsProvider";
import { resolveActiveView, VIEW_QUERY_PARAM } from "./resolveActiveView";

/**
 * The active Dispatch view and every view the cascade offers, with `?view=` as the selection's home.
 */
export function useDispatchView(): {
  view: DispatchViewDefinition | null;
  views: DispatchViewDefinition[];
  selectView: (viewId: string) => void;
} {
  const { settings } = useWebSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedId = searchParams.get(VIEW_QUERY_PARAM);
  const { views, defaultViewId } = settings.dispatch;

  const view = useMemo(() => resolveActiveView(views, defaultViewId, requestedId), [views, defaultViewId, requestedId]);
  const viewList = useMemo(() => Object.values(views), [views]);

  const selectView = useCallback(
    (viewId: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(VIEW_QUERY_PARAM, viewId);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { view, views: viewList, selectView };
}
